import {DateTimeFormatter, LocalDate} from '@js-joda/core';
import IconButton from '@material-ui/core/IconButton';
import Paper from '@material-ui/core/Paper';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import InfoIcon from '@material-ui/icons/Info';
import React, {useMemo} from 'react';
import MissMinutesTimely from "../Components/MissMinutesTimely";
import CapacityInfoModal from '../Modal/CapacityInfoModal'
import {
    ClientServiceTeam,
    EmployeeSchedule,
    getEightWeeks,
    Project,
    UNKNOWN_TEAM
} from '../Utils/EmployeeSchedulesCompiler';
import {Reason} from '../Utils/ReasonsReducer';
import AddProjectButton from './AddProjectButton';
import Capacity from "./Capacity";
import EmployeeTypeIcon from './EmployeeTypeIcon';
import FavoriteButton from './FavoriteButton';
import HolidayRow from "./HolidayRow";
import ProjectGroup from './ProjectGroup';
import ReasonButton from './ReasonButton';
import RefreshButton from './RefreshButton';
import TotalsRow from './TotalsRow';

const useStyles = makeStyles(theme => ({
    table: {
        '& .MuiTableCell-sizeSmall': {
            padding: '6px 12px 6px 8px',
        },
    },
    corner: {
        display: 'flex',
        alignItems: 'center',
    },
    employeeName: {
        flexGrow: 1,
        fontSize: theme.typography.h6.fontSize,
    },
    timelyImage: {
        paddingLeft: '10px',
        marginTop: '-7px',
        marginBottom: '-13px',
    },
    associateName: {
        maxWidth: 'calc(100% - 60px)',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        width: 'fit-content',
        whiteSpace: 'nowrap',
        display: 'inline-block',
        paddingTop: '5px',
    },
    currentHeader: {
        width: 100,
    },
    futureHeader: {
        width: 100,
    },
    header: {
        backgroundColor: theme.custom.tableHeader,
        '& th': {
            color: theme.palette.text.primary,
            fontWeight: 500,
        },
    },
    headerDate: {
        textAlign: 'center',
        lineHeight: '1.2rem',
    },
    headerSub: {
        textAlign: 'center',
        fontWeight: 'normal',
        lineHeight: '1.2rem',
    },
    reason: {
        marginLeft: theme.spacing(0.25),
    },
}));

export type TableScheduleProps = {
    startDate : LocalDate;
    currentWeek : LocalDate;
    employeeSchedule : EmployeeSchedule;
    reasons : Reason[] | null;
    actualsLoaded : boolean;
    favorite ?: {
        isFavorite : boolean;
        onFavoriteChange : (employeeId : number, isFavorite : boolean) => void;
    };
    onProjectViewClick ?: (project : Project) => void;
    highlightPhrase ?: string;
    teams : ClientServiceTeam[];
};

const dateFormatter = DateTimeFormatter.ofPattern('M/d');
const mainProjectTypes = ['Off Contract', 'Service Contract', 'Project', 'Recurring Support', 'Soliant.cloud Services', 'Milestone Project'];

const TableSchedule : React.FC<TableScheduleProps> = ({
    startDate,
    currentWeek,
    employeeSchedule: {employee, schedules},
    reasons,
    actualsLoaded,
    favorite,
    onProjectViewClick,
    highlightPhrase,
    teams,
} : TableScheduleProps) => {
    const classes = useStyles();
    const [capacityInfoOpen, setCapacityInfoOpen] = React.useState(false);
    const team = useMemo(() => {
        let primaryTeam = teams.find((team) => {
            return team.associates.find((associate) => associate.reporting && associate.associateId === employee.id)
        });
        if (!primaryTeam) {
            primaryTeam = teams.find((team) => {
                return team.associates.find((associate) =>associate.associateId === employee.id)
            })
        }
        return primaryTeam ?? UNKNOWN_TEAM;
    }, [employee.id, teams]);

    const secondaryTeams = useMemo(() => {
        return teams.filter((team) => {
            return team.associates.find((associate) =>associate.associateId === employee.id)
        }).filter((secondTeam) => secondTeam.id !== team?.id);
    }, [employee.id, team.id, teams]);

    const weeks = useMemo(() => getEightWeeks(startDate), [startDate]);
    const reasonLimit = useMemo(() => currentWeek.minusWeeks(2), [currentWeek]);

    const [mainSchedules, internalSchedules] = useMemo(() => ([
        schedules.filter(schedule => mainProjectTypes.includes(schedule.project.type)),
        schedules.filter(schedule => !mainProjectTypes.includes(schedule.project.type)),
    ]), [schedules]);

    const timeOff = useMemo(() => {
        const foundTimeOff = internalSchedules.find(schedule => schedule.project.id === 529);

        if (foundTimeOff) {
            return foundTimeOff.weeks;
        }

        return [];
    }, [internalSchedules]);

    return (
        <TableContainer component={Paper}>
            <Table size="small" className={classes.table}>
                <TableHead className={classes.header}>
                    <TableRow>
                        <TableCell>
                            <div className={classes.corner}>
                                {favorite && (
                                    <FavoriteButton
                                        employeeId={employee.id}
                                        isFavorite={favorite.isFavorite}
                                        onFavoriteChange={favorite.onFavoriteChange}
                                    />
                                )}
                                <EmployeeTypeIcon employee={employee}/>
                                <div
                                    className={classes.employeeName}
                                    title={secondaryTeams.map((team) => team.abbreviation).join(String.fromCharCode(10))}
                                >
                                    <span className={classes.associateName}>{team.abbreviation} {employee.firstName} {employee.lastName}</span>
                                    {employee.isTimely && (<>
                                        <MissMinutesTimely
                                            currentTimelyStreak={employee.currentTimelyStreak}
                                            className={classes.timelyImage}
                                            height={50}
                                        />
                                    </>)}
                                </div>
                                <RefreshButton employee={employee} startDate={startDate}/>
                                <AddProjectButton
                                    employee={employee}
                                    schedules={schedules}
                                    startDate={startDate}
                                    teams={teams}
                                />
                            </div>
                        </TableCell>
                        {weeks.map(week => (
                            <TableCell
                                key={week.key}
                                className={week.startDate <= currentWeek ? classes.currentHeader : classes.futureHeader}
                            >
                                <div className={classes.headerDate}>{week.startDate.format(dateFormatter)}</div>
                                <div className={classes.headerSub}>
                                    Sch{week.startDate <= currentWeek && '/Act'}

                                    {week.startDate >= reasonLimit && week.startDate <= currentWeek && (
                                        <span className={classes.reason}>
                                            <ReasonButton
                                                reasons={reasons}
                                                employeeId={employee.id}
                                                startOfWeek={week.key}
                                            />
                                        </span>
                                    )}
                                </div>
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {mainSchedules.length > 0 && (
                        <ProjectGroup
                            employeeId={employee.id}
                            projectSchedules={mainSchedules}
                            subTotalLabel="Sub-Total Project Hours"
                            startDate={startDate}
                            currentWeek={currentWeek.toString()}
                            onProjectViewClick={onProjectViewClick}
                            highlightPhrase={highlightPhrase}
                            teams={teams}
                            isInternal={false}
                        />
                    )}
                    <Capacity
                        projectSchedules={schedules}
                        label={<>Suggested Weekly Capacity <IconButton
                            size="small"
                            edge="start"
                            onClick={() => setCapacityInfoOpen(true)}
                        >
                            <InfoIcon fontSize="small"/>
                        </IconButton></>}
                        employeeId={employee.id}
                        weeks={weeks}
                        timeOff={timeOff}
                    />
                    {internalSchedules.length > 0 ? (
                        <ProjectGroup
                            employeeId={employee.id}
                            projectSchedules={internalSchedules}
                            subTotalLabel="Sub-Total Internal Hours"
                            startDate={startDate}
                            currentWeek={currentWeek.toString()}
                            onProjectViewClick={onProjectViewClick}
                            highlightPhrase={highlightPhrase}
                            teams={teams}
                            isInternal={true}
                        />
                    ) : <HolidayRow
                        employeeId={employee.id}
                        startDate={startDate}
                        currentWeek={currentWeek.toString()}
                    />}
                    <TotalsRow projectSchedules={schedules} label="Total Hours" addHolidays={true}/>
                </TableBody>
            </Table>
            <CapacityInfoModal handleClose={setCapacityInfoOpen} open={capacityInfoOpen}/>
        </TableContainer>
    );
};

export default TableSchedule;
